import {
  DiagnosticResult,
  MaturityLevel,
  Recommendation,
  SurveyAnswers,
} from '../types/survey'
import { surveyBlocks } from './surveyQuestions'

const calculateScore = (answers: SurveyAnswers): number => {
  let score = 0

  surveyBlocks.forEach((block) => {
    block.questions.forEach((q) => {
      // Check if question was answered
      if (answers[q.id]) {
        // Find the selected option
        const option = q.options.find((opt) => opt.id === answers[q.id])
        if (option) {
          score += option.value
        }
      }
    })
  })

  return score
}

const getMaxScore = (): number => {
  // This is a simplified max score calculation
  // In a real app, we'd need to account for conditional paths
  // Here we assume the "best" path for max score
  return 21 // Approximate max score based on values assigned
}

const getMaturityLevel = (score: number, maxScore: number): MaturityLevel => {
  const percentage = (score / maxScore) * 100

  if (percentage < 25) return 'Inicial'
  if (percentage < 50) return 'Básico'
  if (percentage < 75) return 'Intermediário'
  return 'Avançado'
}

export const generateDiagnostic = (
  answers: SurveyAnswers,
): DiagnosticResult => {
  const score = calculateScore(answers)
  const maxScore = getMaxScore()
  const level = getMaturityLevel(score, maxScore)

  const recommendations: Recommendation[] = []

  // Logic 1: Marca em risco (Q7)
  if (['nao_pretendo_sim', 'nao_pretendo_nao'].includes(answers['q7'])) {
    recommendations.push({
      id: 'rec_marca',
      type: 'critical',
      title: 'Sua marca está em risco',
      content:
        'Sua marca não está protegida. O registro na Junta Comercial não garante exclusividade da marca. Consulte o POP 01 do Manual da ACES sobre Registro de Marca imediatamente para evitar perder o nome do seu negócio.',
    })
  }

  // Logic 2: Diferença Junta vs INPI (Q5)
  if (['nao', 'duvidas'].includes(answers['q5'])) {
    recommendations.push({
      id: 'rec_educacao_inpi',
      type: 'warning',
      title: 'Conceito Fundamental: Junta Comercial ≠ INPI',
      content:
        'Muitos empresários confundem a Razão Social (Junta Comercial) com a Marca (INPI). A Razão Social é o nome formal da empresa, enquanto a Marca é como o cliente te reconhece. Apenas o registro no INPI garante a propriedade da marca em todo o território nacional.',
    })
  }

  // Logic 3: Segredos de Negócio e NDA (Q8 & Q9)
  if (answers['q8'] === 'sim' && answers['q9'] === 'nao_nunca') {
    recommendations.push({
      id: 'rec_nda',
      type: 'critical',
      title: 'Proteja seus Segredos de Negócio',
      content:
        'Você identificou possuir segredos de negócio, mas não utiliza contratos de confidencialidade (NDA). Isso deixa sua empresa vulnerável a vazamentos por ex-funcionários ou parceiros. Implemente cláusulas de confidencialidade em seus contratos de trabalho e parcerias.',
    })
  }

  // Logic 4: Pesquisa Prévia (Q10 & Q11)
  if (answers['q10'] === 'sim' && answers['q11'] === 'nao_pesquisou') {
    recommendations.push({
      id: 'rec_busca_anterioridade',
      type: 'warning',
      title: 'Risco de Infração de PI',
      content:
        'Lançar produtos ou marcas sem pesquisa prévia (Busca de Anterioridade) gera alto risco de processar ou ser processado por violar direitos de terceiros. Antes de qualquer lançamento, realize buscas no banco de dados do INPI, não apenas no Google.',
    })
  } else if (answers['q10'] === 'sim' && answers['q11'] === 'so_google') {
    recommendations.push({
      id: 'rec_busca_inpi',
      type: 'info',
      title: 'Aprofunde suas pesquisas',
      content:
        'Pesquisar no Google é um bom começo, mas não é suficiente. Marcas registradas podem não ter site ativo. Utilize a busca oficial do INPI para garantir segurança jurídica.',
    })
  }

  // Logic 5: Estratégia (Q12)
  if (answers['q12'] === 'nunca' || answers['q12'] === 'raramente') {
    recommendations.push({
      id: 'rec_cultura',
      type: 'info',
      title: 'Incorpore PI na Estratégia',
      content:
        'A Propriedade Intelectual não é apenas burocracia, é um ativo valioso. Comece a incluir discussões sobre proteção de ativos intangíveis nas suas reuniões de planejamento trimestrais.',
    })
  }

  // Default positive reinforcement if doing well
  if (recommendations.length === 0 && score > 10) {
    recommendations.push({
      id: 'rec_parabens',
      type: 'success',
      title: 'Boas Práticas Identificadas',
      content:
        'Sua empresa demonstra um bom nível de maturidade em PI. Continue monitorando seus ativos e considere expandir sua proteção para outros tipos de propriedade intelectual (como Desenho Industrial ou Patentes, se aplicável).',
    })
  }

  return {
    score,
    maxScore,
    level,
    recommendations,
  }
}
